import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './styles/VideoEditor.css';
import tooltipTranslations from './dictionary/translateTooltip';
import planTranslations from './dictionary/translatePlan';
import { translations } from './dictionary/translateVideoEditor';
import { adTranslations } from './dictionary/translateAdSimulator';

function PlanSelection({ onLogout }) {
  // Estados e referências
  const [, setUsername] = useState('');
  const [pageLanguage, setPageLanguage] = useState('en');
  const [error, setError] = useState('');
  const [showAd, setShowAd] = useState(false);

  // Billing cycles
  const [billingCycles, setBillingCycles] = useState({
    free: 'monthly',
    starter: 'monthly',
    growth: 'monthly',
    premium: 'monthly',
  });

  // Controle de simulação de anúncio
  const isSimulation = process.env.REACT_APP_AD_SIMULATION === 'true';

  // Controle de áudio de fundo
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  // ***********************
  // 1) DISPARAR CONVERSÃO
  // ***********************
  useEffect(() => {
    // Só dispara se existir a função gtag (garantia de que o script foi carregado)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        // Substitua pela sua label (rótulo) de conversão, se for diferente
        send_to: 'AW-16879755941/SxT4CIOfx6IaEKXF8vA-',
      });
    }
  }, []);
  // *************************************************************

  // Ao montar, carrega user/idioma
  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem('user'));
    setUsername(storedUser?.email || '');
    setPageLanguage(sessionStorage.getItem('pageLanguage') || 'en');
  }, []);

  // Toggle do mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      if (!isMuted) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current.play().catch((err) => {
          console.error('Falha ao tocar o áudio:', err);
        });
      }
    }
  };

  // Mudar o ciclo de cobrança
  const handleBillingCycleChange = (planKey, value) => {
    setBillingCycles((prev) => ({ ...prev, [planKey]: value }));
  };

  // Criar sessão de checkout (Stripe) e redirecionar
  const handlePlanClick = async (planType, planKey) => {
    try {
      const storedUser = JSON.parse(sessionStorage.getItem('user'));
      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/users/checkout-session`,
        {
          email: storedUser?.email,
          planType,
          billingCycle: billingCycles[planKey],
          currency: pageLanguage,
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Error redirecting to payment.');
      }
    } catch (err) {
      setError('Error creating the payment session.');
    }
  };

  // Lógica de exibir anúncio e depois fazer logout
  const handleLogout = () => {
    setShowAd(true);

    if (isSimulation) {
      // Simulação local: mostra anúncio por 5s e faz logout
      setTimeout(() => {
        setShowAd(false);
        onLogout();
      }, 5000);
    } else {
      // Produção: espera anúncio carregar e exibe por 5s
      const checkAdLoaded = () => {
        const adElement = document.querySelector('.adsbygoogle');
        if (adElement && adElement.offsetHeight > 0) {
          setTimeout(() => {
            setShowAd(false);
            onLogout();
          }, 5000);
        } else {
          setTimeout(checkAdLoaded, 500);
        }
      };
      checkAdLoaded();
    }
  };

  // Mostrar anúncio antes de prosseguir ao checkout
  const showAdAndProceed = (planType, planKey) => {
    setShowAd(true);

    if (isSimulation) {
      setTimeout(() => {
        setShowAd(false);
        handlePlanClick(planType, planKey);
      }, 5000);
    } else {
      const checkAdLoaded = () => {
        const adElement = document.querySelector('.adsbygoogle');
        if (adElement && adElement.offsetHeight > 0) {
          setTimeout(() => {
            setShowAd(false);
            handlePlanClick(planType, planKey);
          }, 5000);
        } else {
          setTimeout(checkAdLoaded, 500);
        }
      };
      checkAdLoaded();
    }
  };

  // Forçar render do AdSense quando showAd=true e não está em simulação
  useEffect(() => {
    if (showAd && !isSimulation) {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    }
  }, [showAd, isSimulation]);

  // Tenta iniciar o áudio ao montar
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.play().catch((err) => {
        console.error('Erro ao iniciar o áudio automaticamente:', err);
      });
    }
  }, []);

  // Traduções
  const t = tooltipTranslations[pageLanguage] || tooltipTranslations.en;
  const p = planTranslations[pageLanguage] || planTranslations.en;
  const v = translations[pageLanguage] || translations.en;
  const s = adTranslations[pageLanguage] || adTranslations.en;

  return (
    <div className="plan-selection">
      {/* Botão para mutar/desmutar áudio */}
      <div onClick={toggleMute} style={{ cursor: 'pointer', marginBottom: '10px' }}>
        <i
          className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}
          style={{ fontSize: '24px', color: 'white' }}
        ></i>
      </div>

      <audio ref={audioRef} style={{ display: 'none' }} loop>
        <source
          src={process.env.REACT_APP_VISION_AI_STREAM_AUDIO_PATH}
          type="audio/mpeg"
        />
        Your browser does not support the audio element.
      </audio>

      <h1 className="plan-title">{p.title}</h1>

      {/* Lista de planos */}
      <div className="plan-container">
        {/* VISION FREE */}
        <div className="plan-option tooltip">
          <img
            src="/buttons/free.jpg"
            alt={t.free.title}
            onClick={() => showAdAndProceed('Vision Free', 'free')}
            className="plan-image"
          />
          <p className="plan-name">{t.free.title}</p>
          <div className="tooltip-text">{t.free.details}</div>
          <div className="plan-billing-options">
            <label>
              <input
                type="radio"
                name="free-cycle"
                value="monthly"
                checked={billingCycles.free === 'monthly'}
                onChange={() => handleBillingCycleChange('free', 'monthly')}
              />
              {p.monthly}
            </label>
          </div>
        </div>

        {/* VISION STARTER */}
        <div className="plan-option tooltip">
          <img
            src="/buttons/starter.jpg"
            alt={t.starter.title}
            onClick={() => handlePlanClick('Vision Starter', 'starter')}
            className="plan-image"
          />
          <p className="plan-name">{t.starter.title}</p>
          <div className="tooltip-text">{t.starter.details}</div>
          <div className="plan-billing-options">
            <label>
              <input
                type="radio"
                name="starter-cycle"
                value="monthly"
                checked={billingCycles.starter === 'monthly'}
                onChange={() => handleBillingCycleChange('starter', 'monthly')}
              />
              {p.monthly}
            </label>
            <label>
              <input
                type="radio"
                name="starter-cycle"
                value="yearly"
                checked={billingCycles.starter === 'yearly'}
                onChange={() => handleBillingCycleChange('starter', 'yearly')}
              />
              {p.yearly}
            </label>
          </div>
        </div>

        {/* VISION GROWTH - DESABILITADO */}
        <div className="plan-option tooltip disabled-plan">
          <img
            src="/buttons/growth.jpg"
            alt={t.growth.title}
            className="plan-image"
          />
          <p className="plan-name">{t.growth.title}</p>
          <div className="tooltip-text">{t.growth.details}</div>
          <p className="plan-unavailable">🚫 {p.unavailable || 'Unavailable for now'}</p>
          <div className="plan-billing-options">
            <label>
              <input
                type="radio"
                name="growth-cycle"
                disabled
              />
              {p.monthly}
            </label>
            <label>
              <input
                type="radio"
                name="growth-cycle"
                disabled
              />
              {p.yearly}
            </label>
          </div>
        </div>

        {/* VISION PREMIUM - DESABILITADO */}
        <div className="plan-option tooltip disabled-plan">
          <img
            src="/buttons/premium.jpg"
            alt={t.premium.title}
            className="plan-image"
          />
          <p className="plan-name">{t.premium.title}</p>
          <div className="tooltip-text">{t.premium.details}</div>
          <p className="plan-unavailable">🚫 {p.unavailable || 'Unavailable for now'}</p>
          <div className="plan-billing-options">
            <label>
              <input
                type="radio"
                name="premium-cycle"
                disabled
              />
              {p.monthly}
            </label>
            <label>
              <input
                type="radio"
                name="premium-cycle"
                disabled
              />
              {p.yearly}
            </label>
          </div>
        </div>
      </div>

      {/* Bloco de anúncio (exibido se showAd=true) */}
      {showAd && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {isSimulation ? (
            // Simulação (ex.: local)
            <div
              style={{
                padding: '20px',
                border: '1px solid #ccc',
                backgroundColor: '#f9f9f9',
                color: '#000',
              }}
            >
              <p>{s.adSimulation}</p>
            </div>
          ) : (
            // Produção: anúncio real
            <>
              <ins
                className="adsbygoogle"
                style={{
                  display: 'inline-block',
                  width: '336px',
                  height: '280px',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                }}
                data-ad-client="ca-pub-6453889750420507"
                data-ad-slot="4680078662"
              ></ins>
              {/* eslint-disable-next-line */}
              <script>{`
                (adsbygoogle = window.adsbygoogle || []).push({});
              `}</script>
            </>
          )}
        </div>
      )}

      {/* Exibe erros, se houver */}
      {error && (
        <div style={{ color: 'red', marginTop: '10px' }}>
          {error}
        </div>
      )}

      {/* Botão de logout */}
      <button
        className="custom-button"
        onClick={handleLogout}
        style={{ marginTop: '20px' }}
      >
        {v.logout}
      </button>
    </div>
  );
}

export default PlanSelection;
